import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  BookOpen, TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, Clock, Lock, BarChart3, ArrowRight,
} from 'lucide-react'
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { accountingApi } from '@/modules/accounting/api/accounting.api'
import { ROUTES } from '@/router/routes'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatXOF = (n: number) =>
  new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(Math.round(n))

const STATUS_CONFIG = {
  OPEN:        { label: 'Ouvert',              color: 'text-[#059669]', bg: 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)]', icon: CheckCircle },
  SOFT_CLOSED: { label: 'Clôture provisoire',  color: 'text-[#D97706]',   bg: 'bg-[#FEF3C7] border-[rgba(245,158,11,0.25)]',   icon: Clock },
  HARD_CLOSED: { label: 'Clôturé',             color: 'text-[var(--tx3)]',   bg: 'bg-[#D6D2EC]/10 border-[var(--tx3)]/20',   icon: Lock },
  REOPENED:    { label: 'Réouvert',            color: 'text-[#7C3AED]',  bg: 'bg-[#EDE9FE] border-[rgba(124,58,237,0.25)]', icon: AlertTriangle },
} as const

// ─── Composants ──────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string
  icon: React.ElementType; color: string
}) {
  return (
    <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <p className="text-sm text-[var(--tx3)]">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-2xl font-bold text-[var(--brand-dark)] mb-1">{value}</p>
      {sub && <p className="text-xs text-[var(--tx3)]">{sub}</p>}
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-[var(--bd)] rounded-lg p-3 text-xs">
      <p className="text-[var(--tx3)] mb-2">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name} : {formatXOF(p.value)}
        </p>
      ))}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AccountingDashboard() {
  const { data: fiscalYears = [], isLoading: loadingFY } = useQuery({
    queryKey: ['fiscal-years'],
    queryFn:  accountingApi.getFiscalYears,
  })

  const currentFY = fiscalYears.find(f => f.status === 'OPEN' || f.status === 'SOFT_CLOSED')
    ?? fiscalYears[0]

  const { data: trialBalance, isLoading: loadingBalance } = useQuery({
    queryKey: ['trial-balance', currentFY?.id],
    queryFn:  () => accountingApi.getTrialBalance(currentFY!.id),
    enabled:  !!currentFY,
  })

  const { data: pendingEntries } = useQuery({
    queryKey: ['entries', 'DRAFT', currentFY?.id],
    queryFn:  () => accountingApi.getEntries({ fiscalYearId: currentFY!.id, status: 'DRAFT', limit: 5 }),
    enabled:  !!currentFY,
  })

  const { data: taxRules = [] } = useQuery({
    queryKey: ['tax-rules'],
    queryFn:  accountingApi.getTaxRules,
  })

  // Calculs KPI depuis la balance
  const revenues  = trialBalance?.lines.filter(l => l.class === 7).reduce((s, l) => s + l.balanceCredit, 0) ?? 0
  const expenses  = trialBalance?.lines.filter(l => l.class === 6).reduce((s, l) => s + l.balanceDebit,  0) ?? 0
  const resultNet = revenues - expenses

  // Données graphique par classe SYSCOHADA
  const chartData = trialBalance
    ? [1, 2, 3, 4, 5, 6, 7].map(cls => ({
        classe: `Cl. ${cls}`,
        débit:  trialBalance.lines.filter(l => l.class === cls).reduce((s, l) => s + l.totalDebit,  0),
        crédit: trialBalance.lines.filter(l => l.class === cls).reduce((s, l) => s + l.totalCredit, 0),
      }))
    : []

  const fyStatus = currentFY ? STATUS_CONFIG[currentFY.status] : null
  const FyIcon   = fyStatus?.icon ?? CheckCircle

  return (
    <div className="space-y-5 nx-fade-up">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Comptabilité</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">SYSCOHADA 2018 · Sénégal · XOF</p>
        </div>
        <div className="flex items-center gap-3">
          {currentFY && fyStatus && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium ${fyStatus.bg} ${fyStatus.color}`}>
              <FyIcon className="w-3 h-3" />
              Exercice {currentFY.year} — {fyStatus.label}
            </div>
          )}
          <Link
            to={ROUTES.ACCOUNTING_ENTRIES}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white text-sm font-medium rounded-lg transition-colors"
          >
            <BookOpen className="w-4 h-4" />
            Nouvelle écriture
          </Link>
        </div>
      </div>

      {/* ── Alerte balance déséquilibrée ────────────────────────────────────── */}
      {trialBalance && !trialBalance.isBalanced && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#FEF2F2] border border-[rgba(239,68,68,0.35)] rounded-xl text-[#DC2626] text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            <strong>Balance déséquilibrée :</strong> Σ Débits ≠ Σ Crédits
            — Écart {formatXOF(Math.abs(trialBalance.totalDebit - trialBalance.totalCredit))}.
            Corrigez avant la clôture.
          </span>
        </div>
      )}

      {/* ── KPIs ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 nx-stagger">
        <KpiCard
          label="Chiffre d'affaires"
          value={loadingBalance ? '…' : formatXOF(revenues)}
          sub="Classe 7 — Produits"
          icon={TrendingUp}
          color="bg-[#10B981]/10 text-[#059669]"
        />
        <KpiCard
          label="Charges totales"
          value={loadingBalance ? '…' : formatXOF(expenses)}
          sub="Classe 6 — Charges"
          icon={TrendingDown}
          color="bg-[#FEF2F2] text-[#DC2626]"
        />
        <KpiCard
          label="Résultat net"
          value={loadingBalance ? '…' : formatXOF(resultNet)}
          sub={resultNet >= 0 ? 'Bénéfice' : 'Perte'}
          icon={BarChart3}
          color={resultNet >= 0 ? 'bg-[#E0F2FE] text-[var(--blue-d)]' : 'bg-[#FFF7ED] text-[#EA580C]'}
        />
        <KpiCard
          label="Écritures en brouillon"
          value={loadingBalance ? '…' : String(pendingEntries?.total ?? 0)}
          sub="À valider"
          icon={Clock}
          color="bg-[#FEF3C7] text-[#D97706]"
        />
      </div>

      {/* ── Graphiques ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Mouvements par classe SYSCOHADA */}
        <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)] mb-4">Mouvements par classe SYSCOHADA</h3>
          {loadingBalance ? (
            <div className="h-48 flex items-center justify-center text-[var(--tx3)] text-sm">Chargement…</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                <XAxis dataKey="classe" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={v => `${(v/1000000).toFixed(0)}M`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                <Bar dataKey="débit"  fill="#3b82f6" radius={[3,3,0,0]} />
                <Bar dataKey="crédit" fill="#10b981" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Exercices fiscaux */}
        <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[var(--brand-dark)]">Exercices fiscaux</h3>
            <Link to="/accounting/fiscal-years" className="text-xs text-[var(--blue-d)] hover:text-blue-300">
              Gérer →
            </Link>
          </div>
          {loadingFY ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="h-12 bg-[#F4F2FB] rounded-lg animate-pulse" />)}
            </div>
          ) : fiscalYears.length === 0 ? (
            <div className="text-center py-8 text-[var(--tx3)] text-sm">
              Aucun exercice créé.<br/>
              <Link to="/accounting/fiscal-years" className="text-[var(--blue-d)] hover:underline">Créer le premier exercice</Link>
            </div>
          ) : (
            <div className="space-y-2">
              {fiscalYears.slice(0, 5).map(fy => {
                const cfg = STATUS_CONFIG[fy.status]
                const Icon = cfg.icon
                return (
                  <div key={fy.id} className="flex items-center justify-between px-3 py-2.5 bg-[var(--surface-2)]/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${cfg.color}`} />
                      <span className="text-sm text-[var(--brand-dark)] font-medium">{fy.label}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color}`}>
                      {cfg.label}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Actions rapides ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: 'Plan comptable',     to: '/accounting/accounts',      icon: BookOpen },
          { label: 'Journal des écritures', to: ROUTES.ACCOUNTING_ENTRIES, icon: BookOpen },
          { label: 'Balance générale',   to: ROUTES.ACCOUNTING_BALANCE,   icon: BarChart3 },
          { label: 'Bilan / CR / TFT',   to: ROUTES.ACCOUNTING_REPORTS,   icon: TrendingUp },
          { label: 'Grand Livre',        to: ROUTES.ACCOUNTING_LEDGER,    icon: BookOpen },
        ].map(item => (
          <Link
            key={item.to}
            to={item.to}
            className="flex flex-col items-center gap-2 p-4 bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl hover:border-blue-500/30 hover:bg-[#F0EDF8] transition-all group"
          >
            <item.icon className="w-5 h-5 text-[var(--tx3)] group-hover:text-[var(--blue-d)] transition-colors" />
            <span className="text-xs text-[var(--tx3)] group-hover:text-[var(--brand-dark)] text-center transition-colors">{item.label}</span>
          </Link>
        ))}
      </div>

      {/* ── Règles fiscales actives ─────────────────────────────────────────── */}
      <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[var(--brand-dark)]">Tax Engine — Règles actives</h3>
          <span className="text-xs text-[var(--tx3)]">CGI SN · XOF</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {taxRules.slice(0, 6).map(rule => (
            <div key={rule.id} className="flex items-center justify-between px-3 py-2 bg-[var(--surface-2)]/30 rounded-lg">
              <div>
                <p className="text-xs font-medium text-[var(--brand-dark)]">{rule.code}</p>
                <p className="text-xs text-[var(--tx3)] truncate max-w-[120px]">{rule.name}</p>
              </div>
              <span className="text-xs font-bold text-[var(--blue-d)]">{parseFloat(String(rule.rate))}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
